/**
 * Hooks d'animation côté front — gestion fine du micro-mouvement.
 *
 * Brutalisme cinétique : on ne décore pas, on rythme. Chaque chiffre qui
 * change défile au lieu de sauter. Chaque transition a une intention.
 * Toute animation < 500ms, ease-out, pas de bounce, pas de spring.
 */

import { useEffect, useRef, useState } from "react";

const DEFAULT_DURATION_MS = 350;

/**
 * Anime un nombre qui change vers sa nouvelle valeur sur quelques frames.
 * Skippe l'animation au premier rendu (valeur initiale immédiate).
 *
 * Utilisé pour les indicateurs : quand le PIB ou la cote d'approbation
 * changent, le chiffre se déroule au lieu de sauter.
 */
export function useAnimatedNumber(
  target: number,
  duration: number = DEFAULT_DURATION_MS,
): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const firstRenderRef = useRef(true);

  useEffect(() => {
    // Premier rendu : pas d'animation, on cale directement.
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      setValue(target);
      return;
    }
    // Si pas de changement : rien à faire.
    if (target === value) return;

    fromRef.current = value;
    startRef.current = null;

    const step = (timestamp: number) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // Ease-out cubic : démarre vite, finit doux.
      const eased = 1 - Math.pow(1 - t, 3);
      const current = fromRef.current + (target - fromRef.current) * eased;
      setValue(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return value;
}
